import { create } from 'zustand';
import { useEffect } from 'react';
import { ContentVerificationStatusType } from '../shared/types/api/contentVerificationStatusType';
import { keycloak } from '../auth/keycloak';
import { useSearchMediaApiV1MediaGet } from '../services/api/media/media';
import { useUserStore } from './userStore';
import { MediaTypeFilterType } from '@/shared/types/api/mediaTypeFilterType';

export type OnboardingStep = 1 | 2 | 3;

interface OnboardingStore {
  currentStep: OnboardingStep;
  step1Completed: boolean;
  step2Completed: boolean;
  step3Completed: boolean;
  isInitialized: boolean;
  setCurrentStep: (step: OnboardingStep) => void;
  resetSteps: () => void;
  getTotalSteps: () => number;
  getCompletedSteps: () => number;
  getProgressPercentage: () => number;
  isStepDisabled: (step: OnboardingStep) => boolean;
}

const initialState: Omit<
  OnboardingStore,
  | 'setCurrentStep'
  | 'resetSteps'
  | 'getTotalSteps'
  | 'getCompletedSteps'
  | 'getProgressPercentage'
  | 'isStepDisabled'
> = {
  currentStep: 1,
  step1Completed: false,
  step2Completed: false,
  step3Completed: false,
  isInitialized: false,
};

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  ...initialState,
  setCurrentStep: (step) => set({ currentStep: step }),
  resetSteps: () => set({ ...initialState }),
  getTotalSteps: () => 3,
  getCompletedSteps: () => {
    const { step1Completed, step2Completed, step3Completed } = get();
    return [step1Completed, step2Completed, step3Completed].filter(Boolean).length;
  },
  getProgressPercentage: () => {
    const completed = get().getCompletedSteps();
    const total = get().getTotalSteps();
    return (completed / total) * 100;
  },
  isStepDisabled: (step) => {
    const { step1Completed, step2Completed } = get();
    switch (step) {
      case 1:
        return false;
      case 2:
        return !step1Completed;
      case 3:
        return !step2Completed;
      default:
        return false;
    }
  },
}));

/**
 * Hook to sync onboarding state with media API and `/me`.
 * - Step 1: User has at least one media (any status)
 * - Step 2: User has at least one VERIFIED media
 * - Step 3: `is_media_onboarded` on `/me` (link created / publisher flow completed)
 */
export function useSyncOnboardingWithMedia(enableSync = true) {
  const isMediaOnboarded = useUserStore((state) => state.user?.is_media_onboarded === true);

  // Only enable queries when user is authenticated
  const isAuthenticated = keycloak.authenticated && !!keycloak.token;

  // Fetch all media to check Step 1
  const { data: allMediaData } = useSearchMediaApiV1MediaGet(
    {
      page_size: 1, // Just need to check if any media exists
    },
    {
      query: {
        enabled: isAuthenticated && enableSync,
        staleTime: 0, // Always fetch fresh data
      },
    }
  );

  // Fetch verified media to check Step 2
  const { data: verifiedMediaData } = useSearchMediaApiV1MediaGet(
    {
      content_verification_status: [ContentVerificationStatusType.VERIFIED],
      media_type: MediaTypeFilterType.private,
      page_size: 1,
    },
    {
      query: {
        enabled: isAuthenticated && enableSync,
        staleTime: 0, // Always fetch fresh data
      },
    }
  );

  useEffect(() => {
    // Only update when both queries have completed
    if (allMediaData !== undefined && verifiedMediaData !== undefined) {
      // Step 1: User needs to have at least one media (any status)
      const hasMedia = allMediaData.total > 0;

      // Step 2: User needs to have at least one VERIFIED media
      const hasVerifiedMedia = verifiedMediaData.total > 0;

      useOnboardingStore.setState({
        step1Completed: hasMedia,
        step2Completed: hasVerifiedMedia,
        step3Completed: isMediaOnboarded,
        isInitialized: true,
      });
    }
  }, [allMediaData, verifiedMediaData, isMediaOnboarded]);
}
